import React, { useEffect, useLayoutEffect, createContext, useState, useCallback, useRef } from 'react';
import AuthService from '../../api/services/AuthService';
import LoaderHelper from '../../customComponents/Loading/LoaderHelper';
import { alertErrorMessage } from '../../customComponents/CustomAlertMessage';

const REFERRAL_REWARD_STATUS_KEY = "referralRewardStatus";

const readReferralRewardStatusFromStorage = () => {
  try {
    const raw = localStorage.getItem(REFERRAL_REWARD_STATUS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const ProfileContext = createContext();

export function ProfileProvider(props) {
  const [userDetails, setUserDetails] = useState({});
  const [loginDetails, setLoginDetails] = useState([]);
  const [newStoredTheme, setNewStoredTheme] = useState('');
  const [lastLogin, setLastLogin] = useState("");
  const [currentPage, setCurrentPage] = useState("Dashboard");
  const [themeUpdated, setThemeUpdated] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    return storedTheme !== 'dark';
  });
  const [refreshModal, setRefreshModal] = useState(true);
  const [refreshNotification, setRefreshNotification] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  // Shared state for APIs that should only run once after login
  const [estimatedPortfolio, setEstimatedPortfolio] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [notificationCounts, setNotificationCounts] = useState({ unseen: 0 });
  const [pairs, setPairs] = useState([]);
  const [walletTypes, setWalletTypes] = useState([]);

  const [referralRewardStatus, setReferralRewardStatus] = useState(() => {
    if (!localStorage.getItem("token")) return null;
    return readReferralRewardStatusFromStorage();
  });

  // Refs to track if APIs have been called
  const isInitialized = useRef(false);

  const token = localStorage.getItem('token');

  const updateModelHideStatus = (value) => {
    localStorage.setItem(value, true);
    setRefreshModal(!refreshModal);
  };

  const handleUserDetails = useCallback(async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getDetails();
      if (result?.success) {
        setUserDetails(result?.data);
      } else {
        alertErrorMessage(result?.message);
      }
    } catch (error) {
      // Silent fail
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  const activityLogs = useCallback(async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getActivityLogs(0, 1);
      if (result?.success) {
        setLastLogin(result?.data[0]?.createdAt);
      }
    } catch {
      // Silent fail
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  // Fetch estimated portfolio - shared across components
  const fetchEstimatedPortfolio = useCallback(async (type = "") => {
    try {
      const result = await AuthService.estimatedPortfolio(type);
      if (result?.success) {
        setEstimatedPortfolio(result?.data || {});
        return result?.data;
      }
    } catch {
      // Silent fail
    }
    return null;
  }, []);

  // Refresh portfolio (for manual refresh from components)
  const refreshEstimatedPortfolio = useCallback(async (type = "") => {
    return await fetchEstimatedPortfolio(type);
  }, [fetchEstimatedPortfolio]);

  // Fetch notifications - shared across components
  const fetchNotifications = useCallback(async () => {
    try {
      const result = await AuthService.notifications();
      if (result?.success) {
        setNotifications(result?.data?.reverse() || []);
        setNotificationCounts(result?.counts || { unseen: 0 });
        return result;
      }
    } catch {
      // Silent fail
    }
    return null;
  }, []);

  // Fetch pairs - shared across components
  const fetchPairs = useCallback(async () => {
    try {
      const result = await AuthService.getPairs();
      if (result?.success) {
        setPairs(result?.data || []);
        return result?.data;
      }
    } catch {
      // Silent fail
    }
    return null;
  }, []);

  // Fetch wallet types - shared across components
  const fetchWalletTypes = useCallback(async () => {
    try {
      const result = await AuthService.availableWalletTypes();
      if (result?.success) {
        setWalletTypes(result?.data || []);
        return result?.data;
      }
    } catch {
      // Silent fail
    }
    return null;
  }, []);

  const applyReferralRewardStatus = useCallback((data) => {
    if (data && typeof data === "object") {
      const socialTasksRequired =
        Number(data.socialTasksRequired) > 0
          ? Number(data.socialTasksRequired)
          : Number(data.total) > 0
            ? Number(data.total)
            : 6;
      const socialLinkTasksRequired =
        Number(data.socialLinkTasksRequired) > 0 ? Number(data.socialLinkTasksRequired) : 5;
      const total =
        Number(data.total) > 0 ? Number(data.total) : socialTasksRequired;
      const merged = { ...data, socialTasksRequired, socialLinkTasksRequired, total };
      setReferralRewardStatus(merged);
      localStorage.setItem(REFERRAL_REWARD_STATUS_KEY, JSON.stringify(merged));
    } else {
      setReferralRewardStatus(null);
      localStorage.removeItem(REFERRAL_REWARD_STATUS_KEY);
    }
  }, []);

  const fetchReferralRewardStatus = useCallback(async () => {
    const t = localStorage.getItem("token");
    if (!t) return null;
    try {
      const result = await AuthService.getReferralRewardStatus();
      if (result?.success && result?.data) {
        applyReferralRewardStatus(result.data);
        return result.data;
      }
    } catch {
      // silent
    }
    return null;
  }, [applyReferralRewardStatus]);

  // Generate socket IDs
  const generateSocketUniqueId = useCallback((length = 15) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let uniqueId = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      uniqueId += characters.charAt(randomIndex);
    }
    localStorage.setItem("socketId", uniqueId);
  }, []);

  const generateFutureSocketUniqueId = useCallback((length = 15) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let uniqueId = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      uniqueId += characters.charAt(randomIndex);
    }
    localStorage.setItem("socketIdFuture", uniqueId);
  }, []);

  // Initialize all APIs once after login
  useEffect(() => {
    // Always generate socket IDs
    generateSocketUniqueId();
    generateFutureSocketUniqueId();

    // Only fetch user-specific data if logged in and not already initialized
    if (token && !isInitialized.current) {
      isInitialized.current = true;
      
      // Run all initial API calls
      handleUserDetails();
      activityLogs();
      fetchEstimatedPortfolio("");
      fetchNotifications();
      fetchPairs();
      fetchWalletTypes();
    }

    // Reset initialization flag when token is cleared (logout)
    if (!token) {
      isInitialized.current = false;
      setEstimatedPortfolio({});
      setNotifications([]);
      setNotificationCounts({ unseen: 0 });
      setPairs([]);
      setWalletTypes([]);
      setReferralRewardStatus(null);
      localStorage.removeItem(REFERRAL_REWARD_STATUS_KEY);
    }
  }, [token, handleUserDetails, activityLogs, fetchEstimatedPortfolio, fetchNotifications, fetchPairs, fetchWalletTypes, generateSocketUniqueId, generateFutureSocketUniqueId]);

  useEffect(() => {
    if (token && !readReferralRewardStatusFromStorage()) {
      fetchReferralRewardStatus();
    }
  }, [token, fetchReferralRewardStatus]);

  // Refresh notifications when refreshNotification flag changes
  useEffect(() => {
    if (token && refreshNotification) {
      fetchNotifications();
    }
  }, [refreshNotification, token, fetchNotifications]);

  const handleTheme = () => {
    const htmlTag = document.documentElement;
    const currentTheme = htmlTag.getAttribute('data-theme');
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    localStorage.setItem('theme', newTheme);
    htmlTag.setAttribute('data-theme', newTheme);
    setNewStoredTheme(newTheme);
  };

  useEffect(() => {
    const storedTheme = localStorage.getItem('theme');
    const htmlTag = document.documentElement;
    const resolvedTheme = storedTheme === 'dark' ? 'dark' : 'light';
    if (!storedTheme) {
      localStorage.setItem('theme', 'light');
      localStorage.setItem('CurrentTheme', 'light');
    }
    htmlTag.setAttribute('data-theme', resolvedTheme);
  }, []);

  // Sync themeUpdated to DOM and localStorage (runs before paint so body has class immediately)
  useLayoutEffect(() => {
    const htmlTag = document.documentElement;
    document.body.classList.add('theme-switching');
    if (themeUpdated) {
      document.body.classList.add('light_theme');
      localStorage.setItem('theme', 'light');
      htmlTag.setAttribute('data-theme', 'light');
    } else {
      document.body.classList.remove('light_theme');
      localStorage.setItem('theme', 'dark');
      htmlTag.setAttribute('data-theme', 'dark');
    }

    const resetSwitchClass = window.setTimeout(() => {
      document.body.classList.remove('theme-switching');
    }, 320);

    return () => {
      window.clearTimeout(resetSwitchClass);
    };
  }, [themeUpdated]);

  return (
    <ProfileContext.Provider value={{
      // Existing values
      refreshNotification,
      setRefreshNotification,
      updateModelHideStatus,
      currentPage,
      setCurrentPage,
      lastLogin,
      userDetails,
      setUserDetails,
      handleUserDetails,
      setLoginDetails,
      loginDetails,
      handleTheme,
      newStoredTheme,
      activeTab,
      setActiveTab,
      themeUpdated,
      setThemeUpdated,
      
      // New shared state
      estimatedPortfolio,
      refreshEstimatedPortfolio,
      notifications,
      notificationCounts,
      fetchNotifications,
      pairs,
      fetchPairs,
      walletTypes,
      fetchWalletTypes,

      referralRewardStatus,
      fetchReferralRewardStatus,
      applyReferralRewardStatus,
    }}>
      {props.children}
    </ProfileContext.Provider>
  );
}
